import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, X, CheckCircle2, Sparkles } from 'lucide-react'
import { useOrionAIContext } from '../contexts/OrionAIContext'
import { useOrionAI } from '../hooks/useOrionAI'
import api, { workerAuthHeaders } from '../services/api'
import { useToast } from './Toast'

// Web Worker support (with fallback)
let AIWorker = null
let workerSupported = false

try {
  if (typeof Worker !== 'undefined') {
    try {
      AIWorker = new Worker(new URL('../workers/aiWorker.js', import.meta.url), { type: 'module' })
      workerSupported = true
      // Make worker globally available
      if (typeof window !== 'undefined') {
        window.AIWorker = AIWorker
      }
    } catch (e) {
      console.warn('[OrionGuardian] Web Worker creation failed, using fallback:', e)
      workerSupported = false
    }
  }
} catch (e) {
  console.warn('[OrionGuardian] Web Workers not supported, using fallback')
  workerSupported = false
}

/**
 * Orion Guardian Component
 * 
 * Proactive AI that monitors user actions and detects business logic errors.
 * Shows non-intrusive alerts with explanations and "Fix it" buttons.
 */
export function OrionGuardian({ enabled = true }) {
  const { t } = useTranslation()
  const { aiContext } = useOrionAIContext()
  const { executeAICommand, aiAvailable } = useOrionAI(aiContext)
  const [alerts, setAlerts] = useState([])
  const [nietUitgevoerd, setNietUitgevoerd] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const toast = useToast()
  const analysisTimeoutRef = useRef(null)
  const lastAnalysisRef = useRef(null)
  const workerRef = useRef(AIWorker)

  /**
   * Analyze current state for business logic errors
   * Uses Web Worker if available for non-blocking processing
   */
  const analyzeState = useCallback(async (stateSnapshot) => {
    if (!enabled || !aiAvailable) return

    // Debounce: don't analyze more than once per 2 seconds
    const now = Date.now()
    if (lastAnalysisRef.current && now - lastAnalysisRef.current < 2000) {
      return
    }
    lastAnalysisRef.current = now

    // Clear any pending timeout
    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current)
    }

    // Use setTimeout to ensure non-blocking
    analysisTimeoutRef.current = setTimeout(async () => {
      setIsAnalyzing(true)

      try {
        // Try to use Web Worker if available
        let result

        if (typeof Worker !== 'undefined' && workerRef.current) {
          // Use Web Worker
          const requestId = `analyze_${Date.now()}`
          result = await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              workerRef.current?.removeEventListener('message', handleMessage)
              reject(new Error('Analysis timeout'))
            }, 10000)

            const handleMessage = (event) => {
              if (event.data.requestId === requestId) {
                clearTimeout(timeout)
                workerRef.current?.removeEventListener('message', handleMessage)
                
                if (event.data.type === 'ERROR') {
                  reject(new Error(event.data.error))
                } else if (event.data.type === 'ANALYSIS_RESULT') {
                  resolve(event.data.result)
                } else {
                  resolve(event.data.result || { errors: [], warnings: [], suggestions: [], controle_status: 'niet_uitgevoerd' })
                }
              }
            }

            workerRef.current.addEventListener('message', handleMessage)
            workerRef.current.postMessage({
              type: 'ANALYZE_STATE',
              requestId,
              payload: {
                context: aiContext,
                stateSnapshot: stateSnapshot,
                apiUrl: api.defaults.baseURL || '/api/v1',
                // #681: ook het CSRF-token; zonder weigert de API de POST (403).
                headers: workerAuthHeaders(),
              },
            })
          })
        } else {
          // Fallback: direct API call (non-blocking via setTimeout)
          result = await Promise.race([
            api.post('ai/analyze', {
              context: aiContext,
              state_snapshot: stateSnapshot,
            }).then(res => res.data),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Analysis timeout')), 10000)
            ),
          ])
        }

        // Niet uitgevoerd is iets anders dan niets gevonden (#663)
        setNietUitgevoerd(result.controle_status === 'niet_uitgevoerd'
          ? (result.melding || t('guardian.nietUitgevoerd'))
          : null)

        // Process errors and warnings separately
        const errors = result.errors || []
        const warnings = result.warnings || []
        const allIssues = [...errors, ...warnings]

        if (allIssues.length > 0) {
          // Add new alerts (avoid duplicates based on error_type and key fields)
          setAlerts(prev => {
            const existingKeys = new Set(
              prev.map(a => `${a.error_type}_${a.title}_${JSON.stringify(a.payload || {})}`)
            )
            const newAlerts = allIssues
              .filter(err => {
                const key = `${err.error_type}_${err.title}_${JSON.stringify(err.payload || {})}`
                return !existingKeys.has(key)
              })
              .map(err => ({
                ...err,
                timestamp: new Date().toISOString(),
              }))
            return [...prev, ...newAlerts]
          })
        } else {
          // Clear resolved alerts if no issues found
          setAlerts(prev => {
            // Keep alerts that are still relevant (not auto-dismissed)
            return prev.filter(a => !a.auto_dismiss)
          })
        }
      } catch (error) {
        // Guardian breekt de app niet, maar zegt wel dat de controle niet draaide (#663)
        console.debug('Guardian analysis error (non-critical):', error)
        setNietUitgevoerd(t('guardian.nietUitgevoerd'))
      } finally {
        setIsAnalyzing(false)
      }
    }, 500) // Small delay to batch rapid changes
  }, [enabled, aiAvailable, aiContext, t])

  /**
   * Monitor cabinet state changes
   */
  useEffect(() => {
    if (!enabled) return

    const handleCabinetChange = (event) => {
      const cabinetState = event.detail
      analyzeState({ type: 'cabinet', data: cabinetState })
    }

    const handleFormChange = (event) => {
      const formData = event.detail
      analyzeState({ type: 'form', data: formData })
    }

    // Listen for cabinet state changes
    window.addEventListener('orion-cabinet-state-change', handleCabinetChange)
    window.addEventListener('orion-form-change', handleFormChange)

    return () => {
      window.removeEventListener('orion-cabinet-state-change', handleCabinetChange)
      window.removeEventListener('orion-form-change', handleFormChange)
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current)
      }
    }
  }, [enabled, analyzeState])

  // Route-change-trigger (Sprint AI-5): laat de Guardian bij schermwissel zijn
  // /ai/analyze-aanroep doen op basis van de al beschikbare aiContext. Minimaal —
  // hergebruikt analyzeState (intern gedebounced) + aiContext; geen nieuw event-systeem.
  useEffect(() => {
    if (!enabled) return
    const type = aiContext?.type
    if (type === 'cabinet' && aiContext.cabinet) {
      analyzeState({ type: 'cabinet', data: aiContext.cabinet })
      return
    }
    // Sprint AI-6/WS2: op project-/offerte-scherm projectniveau domein-checks
    // (marge/leverdatum) draaien via het project_id uit de context.
    const projectId = aiContext?.project?.id ?? aiContext?.quote?.project_id
    if (projectId) {
      analyzeState({ type: 'project', data: { project_id: projectId } })
    }
  }, [enabled, aiContext?.route]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle "Fix it" button click
   */
  const handleFix = useCallback(async (alert) => {
    if (!alert.fix_command) {
      toast.error(t('guardian.no_fix_available'))
      return
    }

    try {
      // Parse fix command - string of type "ACTION_TYPE" or "ACTION_TYPE key=val key2=val2"
      let command = alert.fix_command
      const payloadFromAlert = alert.payload || {}

      if (typeof command === 'string') {
        if (command.includes(' ')) {
          const [actionType, ...rest] = command.split(' ')
          const payload = { ...payloadFromAlert }
          rest.forEach(part => {
            const [key, value] = part.split('=')
            if (key && value) {
              payload[key] = isNaN(value) ? value : parseFloat(value)
            }
          })
          command = { type: actionType, payload }
        } else {
          // Simple action type zonder params (payload uit alert)
          command = { type: command, payload: payloadFromAlert }
        }
      }

      const result = await executeAICommand(command, {
        alert_id: alert.id,
        error_type: alert.error_type,
        ...alert.payload,
      })

      if (result?.success !== false) {
        // Remove this alert
        setAlerts(prev => prev.filter(a => a.id !== alert.id))
        const msg = alert.fix_message_key ? t(alert.fix_message_key) : (alert.fix_message || t('guardian.fix_success'))
        toast.success(msg)
        
        // Re-analyze state after fix
        setTimeout(() => {
          analyzeState(aiContext)
        }, 500)
      } else {
        toast.error(result.message || t('guardian.fix_failed'))
      }
    } catch (error) {
      console.error('Error fixing alert:', error)
      toast.error(t('guardian.fix_failed'))
    }
  }, [executeAICommand, toast, analyzeState, aiContext, t])

  /**
   * Dismiss alert
   */
  const handleDismiss = useCallback((alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId))
  }, [])

  if (!enabled || (alerts.length === 0 && !nietUitgevoerd)) {
    return null
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {/* Guardian Status Indicator */}
      {isAnalyzing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center gap-2 text-sm text-blue-700">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span>{t('guardian.analyzing')}</span>
        </div>
      )}

      {/* Controle niet uitgevoerd: nooit als 'geen problemen' tonen (#663) */}
      {nietUitgevoerd && (
        <div
          role="status"
          data-testid="guardian-niet-uitgevoerd"
          className="bg-gray-50 border border-gray-300 rounded-lg p-2 flex items-center gap-2 text-sm text-gray-700"
        >
          <AlertCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="flex-1">{nietUitgevoerd}</span>
          <button
            type="button"
            onClick={() => setNietUitgevoerd(null)}
            className="text-gray-400 hover:text-gray-600"
            aria-label={t('common.close', 'Sluiten')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Alerts */}
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-lg p-4 animate-slide-in"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900 mb-1">
                {alert.title_key ? t(alert.title_key) : (alert.title || 'Waarschuwing')}
              </h4>
              <p className="text-sm text-yellow-800 mb-3">
                {alert.message_key ? t(alert.message_key) : alert.message}
              </p>
              {(alert.explanation_key || alert.explanation) && (
                <p className="text-xs text-yellow-700 mb-3 italic">
                  {alert.explanation_key ? t(alert.explanation_key) : alert.explanation}
                </p>
              )}
              <div className="flex gap-2">
                {alert.fix_command && (
                  <button
                    onClick={() => handleFix(alert)}
                    className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {alert.fix_label_key ? t(alert.fix_label_key) : (alert.fix_label || 'Oplossen')}
                  </button>
                )}
                <button
                  onClick={() => handleDismiss(alert.id)}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  {t('guardian.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// CSS animation (add to global CSS or use Tailwind)
const style = document.createElement('style')
style.textContent = `
  @keyframes slide-in {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`
if (!document.head.querySelector('#orion-guardian-styles')) {
  style.id = 'orion-guardian-styles'
  document.head.appendChild(style)
}
